// src/js/cindy/entry-form.ts
//
// P-18 shared entry-form chrome — the other half of the Shared Lap-Entry Component
// (263-UI-SPEC.md "Component Specification — Shared Lap-Entry Component"). Both
// `src/js/workouts.ts` (263-10, the /workouts/new and /workouts/<id> page form) and
// `src/js/home/cindy-modal.ts` (263-11, the sixth home-dashboard modal) mount
// `mountCindyEntry()` and NOTHING ELSE — this module owns ALL entry markup (date field, paste
// disclosure, round list, `+ Add Round`, leftover-reps, note and the live readouts). Neither
// surface renders any of that markup itself; each supplies only an empty mount <div>.
//
// Deliberately imports nothing from ../home/ or ../workouts (P-18/T-263-12) — only the two
// sibling shared modules below and the app-wide single flatpickr init path.

import { parseCindyNote, ParsedCindyNote } from './lap-parser';
import { mountRoundEntry, RoundEntryController } from './round-entry';
import { initPickers } from '../picker';

export interface CindyEntrySession {
    id: number | null;
    date: string;
    rounds_completed: number;
    partial_reps: number;
    note: string | null;
    rounds: {
        round_no: number;
        duration_display: string;
        pullup_split?: number | null;
        pushup_split?: number | null;
        squat_split?: number | null;
    }[];
}

/** A round's three captured splits, or `null` for a round with no splits. */
type SplitTriple = { pullups: number; pushups: number; squats: number } | null;

export interface CindyEntryController {
    collectPayload(): {
        session_id: number | null;
        date: string;
        rounds: string[];
        rounds_completed: number;
        partial_reps: number;
        note: string;
        // Index-aligned to `rounds` above — `splits[i]` is this round's captured triple or null.
        // The timer (264) is the only producer of these values in this milestone; the form never
        // renders a split input control and never edits one (P-18 pass-through, not editing).
        splits: SplitTriple[];
    };
    refresh(session: CindyEntrySession | null): void; // repopulate in place, never rebuild the root
    destroy(): void;
}

// The operator's own real note format, shown verbatim as the paste-box placeholder so the shape
// is self-explanatory — no separate instructional paragraph needed (263-UI-SPEC.md Copywriting
// Contract).
const PASTE_PLACEHOLDER =
    '### 2026-08-06\n00:43.03\n00:50.19\n…\n-\n19:14.85\n45 pull\n90 push\n135 squats';

let instanceCounter = 0;

/** Empties an element's children without innerHTML (T-263-16 — no interpolated-value innerHTML assignment). */
function clearChildren(el: HTMLElement): void {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}

/**
 * Converts an already-materialized round-duration STRING (not the raw pasted note — that is
 * `parseCindyNote()`'s job and nobody else's) to seconds, for the live Total readout only. This
 * is display-only arithmetic; the server remains the source of truth for anything persisted
 * (T-263-11 — collectPayload() itself performs no validation beyond trimming).
 */
function parseDurationSeconds(text: string): number {
    const trimmed = text.trim();
    if (trimmed === '') return 0;
    if (!trimmed.includes(':')) {
        const n = Number(trimmed);
        return Number.isFinite(n) ? n : 0;
    }
    const [mmss, hh] = trimmed.split('.');
    const parts = mmss.split(':');
    const minutes = Number(parts[0]) || 0;
    const seconds = Number(parts[1]) || 0;
    const hundredths = hh ? Number(hh) / Math.pow(10, hh.length) : 0;
    return minutes * 60 + seconds + (Number.isFinite(hundredths) ? hundredths : 0);
}

/** Formats a total-seconds figure as `MM:SS.hh` for the live Total readout. */
function formatDurationDisplay(totalSeconds: number): string {
    const clamped = Math.max(0, totalSeconds);
    const minutes = Math.floor(clamped / 60);
    const remainder = clamped - minutes * 60;
    const wholeSeconds = Math.floor(remainder);
    const hundredths = Math.round((remainder - wholeSeconds) * 100);
    return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

/** Leftover-reps is clamped 0..29 for display — a full round is 30 reps (5+10+15), so 29 is the ceiling of a genuine partial. */
function clampPartial(raw: string): number {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
    return Math.min(29, Math.max(0, n));
}

/** Open by default when there is nothing to show yet; collapsed when an existing session already has rounds. */
function shouldOpenPasteByDefault(session: CindyEntrySession | null): boolean {
    return session === null || session.rounds.length === 0;
}

/**
 * Seeds the index-aligned splits array from a session's per-round split fields — a triple only
 * when all three movements are non-null for that round, `null` otherwise. Used both at mount and
 * on every refresh() so the two seeding paths can never drift apart.
 */
function seedSplitsFromSession(session: CindyEntrySession | null): SplitTriple[] {
    if (!session) return [];
    return session.rounds.map((r) => {
        const pullups = r.pullup_split ?? null;
        const pushups = r.pushup_split ?? null;
        const squats = r.squat_split ?? null;
        return pullups !== null && pushups !== null && squats !== null ? { pullups, pushups, squats } : null;
    });
}

export function mountCindyEntry(
    rootEl: HTMLElement,
    session: CindyEntrySession | null,
    opts: { context: 'page' | 'modal' }
): CindyEntryController {
    const uid = 'cindy-entry-' + ++instanceCounter;
    rootEl.dataset.cindyContext = opts.context;
    clearChildren(rootEl);

    // 1. Date field — sits above the paste box, `[data-picker="date"]` picked up by the existing
    // initPickers() sweep (single flatpickr init path, called once below after every field this
    // mount creates is in the DOM).
    const dateGroup = document.createElement('div');
    dateGroup.className = 'mb-3';
    const dateLabel = document.createElement('label');
    dateLabel.className = 'form-label';
    dateLabel.htmlFor = uid + '-date';
    dateLabel.textContent = 'Date';
    const dateInput = document.createElement('input');
    dateInput.type = 'text';
    dateInput.id = uid + '-date';
    dateInput.className = 'form-control';
    dateInput.setAttribute('data-picker', 'date');
    dateInput.autocomplete = 'off';
    dateInput.setAttribute('data-max-date', 'today');
    dateInput.required = true;
    dateInput.value = session?.date ?? '';
    dateGroup.appendChild(dateLabel);
    dateGroup.appendChild(dateInput);
    rootEl.appendChild(dateGroup);

    // 2. Paste disclosure — collapsed by default once a session already has rounds, open
    // otherwise (P-04b). Capability is identical either way — only the default open/closed state
    // differs.
    const details = document.createElement('details');
    details.className = 'mb-3 cindy-paste-disclosure';
    details.open = shouldOpenPasteByDefault(session);
    const summary = document.createElement('summary');
    summary.textContent = 'Paste to replace rounds';
    details.appendChild(summary);

    const pasteBody = document.createElement('div');
    pasteBody.className = 'mt-2';

    const pasteLabel = document.createElement('label');
    pasteLabel.className = 'form-label';
    pasteLabel.htmlFor = uid + '-paste';
    pasteLabel.textContent = 'Paste from your notes';
    pasteBody.appendChild(pasteLabel);

    const textarea = document.createElement('textarea');
    textarea.id = uid + '-paste';
    textarea.className = 'form-control cindy-paste-box';
    textarea.rows = 6;
    textarea.autocomplete = 'off';
    textarea.placeholder = PASTE_PLACEHOLDER;
    pasteBody.appendChild(textarea);

    const parseBtn = document.createElement('button');
    parseBtn.type = 'button';
    parseBtn.className = 'btn btn-outline-primary btn-sm mt-2 cindy-parse-btn';
    parseBtn.textContent = 'Parse note';
    pasteBody.appendChild(parseBtn);

    const feedbackEl = document.createElement('div');
    feedbackEl.className = 'cindy-parse-feedback';
    pasteBody.appendChild(feedbackEl);

    details.appendChild(pasteBody);
    rootEl.appendChild(details);

    // 3. Round list container + `+ Add Round` — mounts the shared repeater from round-entry.ts.
    const roundsGroup = document.createElement('div');
    roundsGroup.className = 'mb-3';
    const roundListEl = document.createElement('div');
    roundListEl.className = 'cindy-round-list';
    roundsGroup.appendChild(roundListEl);

    const addRoundBtn = document.createElement('button');
    addRoundBtn.type = 'button';
    addRoundBtn.className = 'btn btn-outline-secondary w-100 mt-2';
    addRoundBtn.textContent = '+ Add Round';
    roundsGroup.appendChild(addRoundBtn);
    rootEl.appendChild(roundsGroup);

    const initialRounds = session ? session.rounds.map((r) => r.duration_display) : [];
    const roundController: RoundEntryController = mountRoundEntry(roundListEl, initialRounds);

    // Module-local split state, index-aligned to roundController's rows. Never rendered by any
    // input control in this form — the live timer (264) is the only producer of these values;
    // this form's job is faithful pass-through to the save payload, not editing.
    let splitsByIndex: SplitTriple[] = seedSplitsFromSession(session);
    let previousDurations: string[] = roundController.getRounds();

    addRoundBtn.addEventListener('click', () => {
        roundController.addRound();
    });

    // Reconciles splitsByIndex against the rounds array on every edit/add/delete. This is a
    // deliberate data-loss decision, not an accident: an add or delete renumbers every row below
    // the change point, and there is no way to tell a shift from an edit by value alone — a
    // mis-aligned split is strictly worse than no split, so a length change drops ALL splits.
    // Otherwise, only the splits at indices whose duration string actually changed are dropped;
    // every untouched round's splits stay intact.
    roundController.onChange(() => {
        const currentDurations = roundController.getRounds();
        if (currentDurations.length !== previousDurations.length) {
            splitsByIndex = currentDurations.map(() => null);
        } else {
            currentDurations.forEach((value, idx) => {
                if (value !== previousDurations[idx]) {
                    splitsByIndex[idx] = null;
                }
            });
        }
        previousDurations = currentDurations;
    });

    // 4. Leftover-reps field.
    const partialGroup = document.createElement('div');
    partialGroup.className = 'mb-3';
    const partialLabel = document.createElement('label');
    partialLabel.className = 'form-label';
    partialLabel.htmlFor = uid + '-partial';
    partialLabel.textContent = 'Leftover reps (optional)';
    const partialInput = document.createElement('input');
    partialInput.type = 'text';
    partialInput.id = uid + '-partial';
    partialInput.className = 'form-control';
    partialInput.setAttribute('inputmode', 'numeric');
    partialInput.autocomplete = 'off';
    partialInput.placeholder = '0';
    partialInput.value = session && session.partial_reps ? String(session.partial_reps) : '';
    partialGroup.appendChild(partialLabel);
    partialGroup.appendChild(partialInput);
    rootEl.appendChild(partialGroup);

    // 5. Note field.
    const noteGroup = document.createElement('div');
    noteGroup.className = 'mb-4';
    const noteLabel = document.createElement('label');
    noteLabel.className = 'form-label';
    noteLabel.htmlFor = uid + '-note';
    noteLabel.textContent = 'Note (optional)';
    const noteInput = document.createElement('input');
    noteInput.type = 'text';
    noteInput.id = uid + '-note';
    noteInput.className = 'form-control';
    noteInput.autocomplete = 'off';
    noteInput.placeholder = 'e.g. legs shot from Tuesday';
    noteInput.value = session?.note ?? '';
    noteGroup.appendChild(noteLabel);
    noteGroup.appendChild(noteInput);
    rootEl.appendChild(noteGroup);

    // 6. Live readouts — read-only, mono/tabular via the .cindy-score / .cindy-stat-value classes
    // (src/css/style.scss, Task 3 of this plan).
    const readouts = document.createElement('div');
    readouts.className = 'cindy-live-readouts mb-2';
    const scoreEl = document.createElement('div');
    scoreEl.className = 'cindy-score';
    const totalEl = document.createElement('div');
    totalEl.className = 'cindy-stat-value';
    readouts.appendChild(scoreEl);
    readouts.appendChild(totalEl);
    rootEl.appendChild(readouts);

    function updateReadouts(): void {
        const rounds = roundController.getRounds();
        const roundsCount = rounds.length;
        const partial = clampPartial(partialInput.value);
        // Always shows +N even when N is 0 — never bare "9" (Copywriting Contract's 9+0 convention).
        scoreEl.textContent = `Score: ${roundsCount}+${partial}`;
        const totalSeconds = rounds.reduce((sum, r) => sum + parseDurationSeconds(r), 0);
        totalEl.textContent = `Total: ${formatDurationDisplay(totalSeconds)}`;
    }

    roundController.onChange(updateReadouts);
    partialInput.addEventListener('input', updateReadouts);
    updateReadouts();

    /** Renders parse feedback per UI-SPEC's Copywriting Contract — errors first, then the checksum line. */
    function renderParseFeedback(result: ParsedCindyNote): void {
        clearChildren(feedbackEl);

        // T-263-16: each error is server/parser-derived text rendered via textContent — never
        // interpolated into innerHTML.
        result.errors.forEach((msg) => {
            const line = document.createElement('div');
            line.className = 'text-danger small mt-1 cindy-inline-error';
            line.textContent = msg;
            feedbackEl.appendChild(line);
        });

        const n = result.rounds.length;
        const checksumLine = document.createElement('div');
        // Three distinct branches keyed on true/false/null — never collapsed into a truthy test,
        // since null (no movement-total lines to check) and false (checksum mismatch) must not
        // share a branch (263-05-PLAN.md Task 2).
        if (result.checksumOk === true) {
            checksumLine.className = 'text-success small mt-1';
            checksumLine.textContent = `Parsed ${n} rounds — checksums match.`;
        } else if (result.checksumOk === false) {
            checksumLine.className = 'text-warning small mt-1';
            const detail = result.checksumDetail;
            const impliedRounds = detail ? detail.impliedRounds : null;
            const pull = detail ? detail.pull : null;
            checksumLine.textContent =
                `Parsed ${n} rounds, but the movement totals imply ${impliedRounds} ` +
                `(${pull} pull ÷ 5). Double-check the '-' line wasn't misread as a lap.`;
        } else if (result.checksumOk === null) {
            checksumLine.className = 'small mt-1';
            checksumLine.textContent = `Parsed ${n} rounds.`;
        }
        feedbackEl.appendChild(checksumLine);
    }

    /** Mirrors home/date-nav.ts:113-116's exact _flatpickr-vs-plain-value branch. */
    function setDateValue(value: string): void {
        if ((dateInput as any)._flatpickr) {
            (dateInput as any)._flatpickr.setDate(value, false);
        } else {
            dateInput.value = value;
        }
    }

    function runParse(): void {
        const raw = textarea.value;
        // The ONLY parsing of the raw pasted blob in this module — everything else operates on
        // already-structured strings the parser (or the operator's own typing) produced.
        const result = parseCindyNote(raw);
        roundController.setRounds(result.rounds);

        if (result.date) {
            if (!dateInput.value) {
                setDateValue(result.date);
            } else if (dateInput.value !== result.date) {
                // P-04b: never silently clobber an intentionally-typed date — confirm first, and
                // leave the field untouched on decline.
                const overwrite = window.confirm(
                    `The pasted note is dated ${result.date}, but the date field already holds ${dateInput.value}. Overwrite it?`
                );
                if (overwrite) {
                    setDateValue(result.date);
                }
            }
        }

        renderParseFeedback(result);
        updateReadouts();
    }

    parseBtn.addEventListener('click', runParse);
    textarea.addEventListener('paste', () => {
        // The native paste hasn't populated textarea.value yet at 'paste' event time — defer to
        // the next tick so runParse() reads the fully-inserted text.
        setTimeout(runParse, 0);
    });

    // Single sanctioned flatpickr init path — called ONCE, after every [data-picker="date"]
    // field this mount created is already in the DOM. Never a direct flatpickr() call here.
    initPickers();

    function collectPayload() {
        // No validation beyond trimming — the server (CindyModel::saveSession(), 263-04/264-03)
        // re-validates every field; duplicating validation rules here is how the two drift
        // (T-263-11).
        return {
            session_id: session?.id ?? null,
            date: dateInput.value.trim(),
            rounds: roundController.getRounds().map((r) => r.trim()),
            rounds_completed: roundController.getRounds().length,
            partial_reps: clampPartial(partialInput.value),
            note: noteInput.value.trim(),
            splits: splitsByIndex.slice(),
        };
    }

    function refresh(newSession: CindyEntrySession | null): void {
        session = newSession;

        setDateValue(newSession?.date ?? '');
        details.open = shouldOpenPasteByDefault(newSession);
        clearChildren(feedbackEl);
        textarea.value = '';

        const rounds = newSession ? newSession.rounds.map((r) => r.duration_display) : [];
        roundController.setRounds(rounds);
        // setRounds() above already fired the reconciliation onChange handler with the STALE
        // previousDurations from before this refresh — reseed from newSession here to override
        // that intermediate result with the authoritative split state for the freshly-loaded
        // session, then resync previousDurations so the next real edit diffs against it correctly.
        splitsByIndex = seedSplitsFromSession(newSession);
        previousDurations = roundController.getRounds();

        partialInput.value = newSession && newSession.partial_reps ? String(newSession.partial_reps) : '';
        noteInput.value = newSession?.note ?? '';

        updateReadouts();
    }

    function destroy(): void {
        clearChildren(rootEl);
    }

    return { collectPayload, refresh, destroy };
}
